import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useRef, useState } from 'react';

import { downloadLinkUrl } from '../../api/client';
import {
  addAttachment,
  addDownload,
  productFileLink,
  removeProductFile,
  reorderProductFiles,
  uploadFile,
} from '../../api/business/endpoints';
import type { ProductAttachment, ProductDetail, ProductDownload } from '../../api/business/types';
import { useAppMutation } from '../../api/useAppMutation';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { ErrorNotice } from '../../components/ErrorNotice';
import { formatBytes, formatNumber } from '../../components/format';
import { acceptedFor, fileProblem, MAX_FILE_MB, movedId } from '../../components/productFiles';
import { useContentLanguages } from '../../components/useContentLanguages';
import { useLanguage } from '../../i18n/LanguageContext';

import { ProductFileDialog } from './ProductFileDialog';

type ProductFilesSectionProps = {
  companyId: string;
  product: ProductDetail;
  /** `downloads` — a virtual product's Na stiahnutie; `attachments` — any product's Prílohy. */
  kind: 'downloads' | 'attachments';
  canManage: boolean;
};

type FileRow = ProductDownload | ProductAttachment;

/**
 * A product's downloads or attachments (R0030 WP-10): the list in order with each file's name, type and size; to
 * whoever may manage products, an upload through the API with its progress, names per language, arrows to
 * reorder and removal. A file opens through a one-time link to the API (R0041) — downloads are what a customer pays for.
 */
export const ProductFilesSection = ({ companyId, product, kind, canManage }: ProductFilesSectionProps) => {
  const { language, t } = useLanguage();
  const { languages } = useContentLanguages();
  const input = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [editing, setEditing] = useState<FileRow | null>(null);
  const [removing, setRemoving] = useState<FileRow | null>(null);
  const rows: Array<FileRow> = kind === 'downloads' ? product.downloads : product.attachments;
  const ids = rows.map((row) => row.id);
  const purpose = kind === 'downloads' ? 'DOWNLOAD' : 'ATTACHMENT';
  const upload = useAppMutation(
    async (file: File) => {
      const { fileId } = await uploadFile(companyId, file, purpose, setProgress);

      return kind === 'downloads'
        ? addDownload(companyId, product.id, { fileId, fileName: file.name })
        : addAttachment(companyId, product.id, { fileId, fileName: file.name });
    },
    ['product'],
    () => setProgress(null),
  );
  const reorder = useAppMutation(
    (next: Array<string>) => reorderProductFiles(companyId, product.id, kind, next),
    ['product'],
  );
  const remove = useAppMutation(
    (id: string) => removeProductFile(companyId, product.id, kind, id),
    ['product'],
    () => setRemoving(null),
  );
  const open = useAppMutation(
    (id: string) => productFileLink(companyId, product.id, kind, id),
    [],
    (link) => window.open(downloadLinkUrl('business', link.path), '_blank', 'noopener'),
  );
  const busy = upload.isPending || reorder.isPending || remove.isPending;

  const choose = (file: File | undefined): void => {
    if (file === undefined) return;

    const refusal = fileProblem(file, purpose);

    if (refusal !== null) {
      setProblem(t(refusal, { maxMb: MAX_FILE_MB }));

      return;
    }

    setProblem(null);
    setProgress(0);
    upload.mutate(file, { onError: () => setProgress(null) });
  };

  const nameOf = (row: FileRow): string => row.names[languages[0]] ?? row.file.fileName;

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        useFlexGap
        spacing={1}
      >
        <Typography variant="h6">
          {t(kind === 'downloads' ? 'files.downloadsTitle' : 'files.attachmentsTitle')}
        </Typography>
        {canManage ? (
          <>
            <input
              ref={input}
              hidden
              type="file"
              accept={acceptedFor(purpose)}
              onChange={(e) => {
                choose(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
            <Button startIcon={<UploadFileIcon />} disabled={busy} onClick={() => input.current?.click()}>
              {t('files.upload')}
            </Button>
          </>
        ) : null}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
        {t(kind === 'downloads' ? 'files.downloadsHint' : 'files.attachmentsHint', { maxMb: MAX_FILE_MB })}
      </Typography>
      {progress !== null ? (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            {t('files.uploading', { percent: formatNumber(Math.round(progress * 100), language) })}
          </Typography>
          <LinearProgress variant="determinate" value={progress * 100} />
        </Box>
      ) : null}
      {problem ? (
        <Alert severity="warning" variant="outlined" sx={{ mb: 1 }} onClose={() => setProblem(null)}>
          {problem}
        </Alert>
      ) : null}
      <ErrorNotice error={upload.error ?? reorder.error ?? open.error} />
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          {t(kind === 'downloads' ? 'files.downloadsEmpty' : 'files.attachmentsEmpty')}
        </Typography>
      ) : (
        <List dense disablePadding>
          {rows.map((row, index) => (
            <ListItem
              key={row.id}
              disableGutters
              secondaryAction={
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title={t('files.open')}>
                    <IconButton size="small" aria-label={t('files.open')} onClick={() => open.mutate(row.id)}>
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {canManage ? (
                    <>
                      <Tooltip title={t('files.edit')}>
                        <IconButton size="small" aria-label={t('files.edit')} onClick={() => setEditing(row)}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('files.moveUp')}>
                        <span>
                          <IconButton
                            size="small"
                            aria-label={t('files.moveUp')}
                            disabled={index === 0 || busy}
                            onClick={() => reorder.mutate(movedId(ids, index, -1))}
                          >
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={t('files.moveDown')}>
                        <span>
                          <IconButton
                            size="small"
                            aria-label={t('files.moveDown')}
                            disabled={index === rows.length - 1 || busy}
                            onClick={() => reorder.mutate(movedId(ids, index, 1))}
                          >
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={t('files.remove')}>
                        <IconButton
                          size="small"
                          aria-label={t('files.remove')}
                          onClick={() => setRemoving(row)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  ) : null}
                </Stack>
              }
            >
              <ListItemAvatar>
                <Avatar variant="rounded">
                  <InsertDriveFileOutlinedIcon />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <span>{nameOf(row)}</span>
                    <Chip size="small" variant="outlined" label={row.file.kind} />
                    {'maxDownloads' in row && row.maxDownloads !== null ? (
                      <Chip
                        size="small"
                        label={t('files.downloadsCount', { count: formatNumber(row.maxDownloads, language) })}
                      />
                    ) : null}
                    {'validityDays' in row && row.validityDays !== null ? (
                      <Chip
                        size="small"
                        label={t('files.daysCount', { count: formatNumber(row.validityDays, language) })}
                      />
                    ) : null}
                  </Stack>
                }
                secondary={[
                  row.file.fileName,
                  formatBytes(row.file.bytes, language),
                  'descriptions' in row ? row.descriptions[languages[0]] : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              />
            </ListItem>
          ))}
        </List>
      )}
      {editing ? (
        <ProductFileDialog
          companyId={companyId}
          productId={product.id}
          file={
            kind === 'downloads'
              ? { kind, item: editing as ProductDownload }
              : { kind: 'attachments', item: editing as ProductAttachment }
          }
          onClose={() => setEditing(null)}
        />
      ) : null}
      {removing ? (
        <ConfirmDialog
          title={t('files.removeTitle')}
          message={t('files.removeMessage', { name: nameOf(removing) })}
          confirmLabel={t('files.remove')}
          onConfirm={() => remove.mutate(removing.id)}
          onClose={() => setRemoving(null)}
          isBusy={remove.isPending}
          error={remove.error}
        />
      ) : null}
    </Paper>
  );
};
